import React from "react";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { formatLocalDate } from "../../../utils/formatLocalDate";
import { buildMonthGrid } from "./slotGrid";

const WEEKDAY_INITIALS = ["M", "T", "W", "T", "F", "S", "S"];

const MONTH_LABEL = (year, month) =>
  new Date(year, month, 1).toLocaleDateString("en-GB", {
    month: "long",
    year: "numeric",
  });

/**
 * The month grid — the bookings dashboard's month view, and the popover the
 * Shifts tab's week picker opens (`selectedKeys` highlights a whole week).
 *
 * Three day states, only one of which the backend knows about:
 *  - marked      the day has something on it (derived from the fetch)
 *  - "Closed"    Sunday, plus days in the past — a frontend product rule
 *  - "Open"      everything else, selectable
 *
 * @param {Object} props
 * @param {number} props.year
 * @param {number} props.month - 0-indexed.
 * @param {(year: number, month: number) => void} props.onMonthChange
 * @param {Date|null} props.selectedDate
 * @param {(date: Date) => void} props.onSelectDate
 * @param {Record<string, Array<Object>>} props.markedByDate - Keyed `YYYY-MM-DD`;
 *   any day with a non-empty array gets a dot.
 * @param {boolean} [props.busy] - A fetch is in flight. The grid still renders
 *   (it needs no data); this only drives a small spinner. Gating the grid on
 *   loading would take the month arrows away with it, making it impossible to
 *   page through months faster than the network.
 * @param {boolean} [props.allowPast=false] - Make days before today selectable.
 *   The past lock is a rule about *publishing* availability, not about the
 *   calendar — looking back at what was already published, or at bookings that
 *   have happened, has to be clickable. Callers that set this are responsible
 *   for keeping the day read-only downstream.
 * @param {boolean} [props.allowClosed=false] - Make Sundays selectable too.
 * @param {string} [props.markLabel="has bookings"] - What a dot means, for the
 *   screen-reader suffix and the legend.
 * @param {Array<string>} [props.selectedKeys] - Several `YYYY-MM-DD` days to show
 *   as selected together — the week picker highlights a whole week row this way.
 *   Takes precedence over `selectedDate`.
 * @param {boolean} [props.showLegend=true] - The Open/marked/Closed key under the grid.
 */
export const SlotMonthCalendar = ({
  year,
  month,
  onMonthChange,
  selectedDate,
  onSelectDate,
  markedByDate,
  busy = false,
  allowPast = false,
  allowClosed = false,
  markLabel = "has bookings",
  selectedKeys,
  showLegend = true,
}) => {
  const weeks = buildMonthGrid(year, month);
  const selectedKey = selectedDate ? formatLocalDate(selectedDate) : null;
  const selectedSet = Array.isArray(selectedKeys) ? new Set(selectedKeys) : null;

  const step = (delta) => {
    const next = new Date(year, month + delta, 1);
    onMonthChange(next.getFullYear(), next.getMonth());
  };

  return (
    <div>
      <div className="flex items-center justify-between rounded-lg bg-light-orange/40 px-2 py-2">
        <button
          type="button"
          onClick={() => step(-1)}
          aria-label="Previous month"
          className="inline-flex h-9 w-9 items-center justify-center rounded-md text-bc-text-2 transition-colors hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-primary"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
        </button>
        <span
          className="inline-flex items-center gap-2 text-sm font-semibold text-bc-text"
          aria-live="polite"
        >
          {MONTH_LABEL(year, month)}
          {/*
            Sits inside the label's own live region rather than announcing
            separately — a screen reader hears the month, not "loading" on
            every arrow press. Fixed-size box so the header never reflows.
          */}
          <span className="inline-flex h-4 w-4 items-center justify-center" aria-hidden="true">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin text-bc-faint" /> : null}
          </span>
        </span>
        <button
          type="button"
          onClick={() => step(1)}
          aria-label="Next month"
          className="inline-flex h-9 w-9 items-center justify-center rounded-md text-bc-text-2 transition-colors hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-primary"
        >
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>

      {/*
        border-collapse + cell padding, NOT border-separate + border-spacing:
        with border-separate the spacing is added *on top of* the table's
        `w-full`, so the table renders ~2rem wider than its container and
        overflows horizontally. Padding stays inside the 100%.
      */}
      <table className="mt-3 w-full table-fixed border-collapse">
        <thead>
          <tr>
            {WEEKDAY_INITIALS.map((initial, index) => (
              <th
                key={`${initial}-${index}`}
                scope="col"
                className="pb-1 text-[11px] font-medium uppercase tracking-wide text-bc-faint"
              >
                {initial}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {weeks.map((week, weekIndex) => (
            <tr key={weekIndex}>
              {week.map((cell, dayIndex) => {
                if (!cell) return <td key={dayIndex} />;

                const marked = (markedByDate?.[cell.key] || []).length > 0;
                const disabled =
                  (cell.isClosed && !allowClosed) || (cell.isPast && !allowPast);
                const isSelected = selectedSet
                  ? selectedSet.has(cell.key)
                  : cell.key === selectedKey;

                let stateClass;
                if (disabled) {
                  stateClass = "cursor-not-allowed bg-light-gray text-bc-faintest";
                } else if (isSelected) {
                  stateClass = "bg-orange-primary font-semibold text-white";
                } else if (cell.isPast) {
                  // Selectable, but visibly secondary: nothing can be published
                  // here, so it must not read as an invitation to try.
                  stateClass = "bg-light-gray/60 text-bc-soft hover:bg-light-gray";
                } else {
                  stateClass =
                    "bg-light-orange text-bc-text hover:bg-light-orange/70";
                }

                return (
                  <td key={dayIndex} className="p-0.5">
                    <button
                      type="button"
                      disabled={disabled}
                      aria-pressed={isSelected}
                      aria-current={cell.isToday ? "date" : undefined}
                      onClick={() => onSelectDate(cell.date)}
                      className={`relative flex h-10 w-full items-center justify-center rounded-md text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-primary focus-visible:ring-offset-1 ${stateClass}`}
                    >
                      {cell.day}
                      {marked && !disabled ? (
                        <span
                          aria-hidden="true"
                          className={`absolute bottom-1 h-1 w-1 rounded-full ${
                            isSelected ? "bg-white" : "bg-orange-primary"
                          }`}
                        />
                      ) : null}
                      {marked ? (
                        <span className="sr-only">, {markLabel}</span>
                      ) : null}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      {showLegend ? (
      <div className="mt-2 flex flex-wrap items-center gap-4 text-[11px] text-bc-soft">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-light-orange" aria-hidden="true" />
          Open
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-orange-primary" aria-hidden="true" />
          {markLabel.charAt(0).toUpperCase() + markLabel.slice(1)}
        </span>
        {/* Nothing is greyed out when Sundays are selectable too, so the key
            would be pointing at a state that isn't on screen. */}
        {allowClosed ? null : (
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-light-gray" aria-hidden="true" />
            Closed
          </span>
        )}
      </div>
      ) : null}
    </div>
  );
};
